"use client";

import { useState } from "react";
import { apiAgentClient } from "services/apiAgentClient";
import { selectUser } from "stores/auth/authSlice";
import { selectBookingStore } from "stores/booking/bookingSlice";
import { useAppSelector } from "stores/hooks";
import { useDisclosure } from "utils/hooks/useDisclosure";
import ShareWithClientModal from "./ShareWithClientModal";
const buildWhiteLabelUrl = (shareUrl: string): string => {
  if (!shareUrl) return "";
  try {
    const url = new URL(shareUrl);
    url.pathname = url.pathname.replace(/^\/detail\//, "/property/");
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return shareUrl;
  }
};
const ShareWithClientButton = ({
  propertyId,
  className,
  label = "Share with a client"
}: {
  propertyId: string;
  className?: string;
  label?: string;
}) => {
  const users = useAppSelector(selectUser);
  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [whiteLabelUrl, setWhiteLabelUrl] = useState("");
  const {
    isOpen,
    onOpen,
    onClose
  } = useDisclosure();
  const {
    startDate,
    endDate,
    prequote
  } = useAppSelector(selectBookingStore);
  const handleShareWithClient = async () => {
    setIsLoading(true);
    try {
      const response = await apiAgentClient.generateShareLink({
        data: {
          propertyId,
          preQuoteId: prequote?.prequoteResponseId || null
        }
      });
      let url = response.data.data.shareUrl;
      const params = new URLSearchParams();
      if (prequote?.prequoteResponseId) {
        params.set("quoteId", prequote.prequoteResponseId);
      }
      if (prequote?.commissionRatePercent != null) {
        params.set("commissionRatePercent", String(prequote.commissionRatePercent));
      }
      if (startDate && endDate) {
        params.set("check_in", startDate);
        params.set("check_out", endDate);
      }
      const extraQuery = params.toString();
      if (extraQuery) {
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}${extraQuery}`;
      }
      setShareUrl(url);
      setWhiteLabelUrl(buildWhiteLabelUrl(url));
      onOpen();
      if (response?.data?.apiStatus === "SUCCESS") {
        void (await (async () => {
          const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
          mixpanelV2Client.TA_LINK_GENERATED({
            agentId: users?.userId || "",
            link: url,
            propertyId
          });
        })());
      }
    } catch (error) {
      console.error("Error generating share link:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    onClose();
    setShareUrl("");
    setWhiteLabelUrl("");
  };
  return <>
      <button id='ShareWithClientButton' className={className ?? "btn h-12 w-full rounded-[8px] border-2 border-[#008292] bg-white text-sm font-bold leading-[28px] text-[#008292] transition-all duration-200 hover:bg-[#008292] hover:text-white focus:bg-[#008292] focus:text-white active:bg-[#008292] active:text-white sm:text-base"} onClick={() => {
      void handleShareWithClient();
      void (async () => {
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        mixpanelV2Client.TA_LINK_GENERATION_CLICK({
          agentId: users?.userId || "",
          propertyId
        });
      })();
    }} disabled={isLoading}>
        {isLoading ? "Generating link..." : label}
      </button>
      <ShareWithClientModal isOpen={isOpen} onClose={handleClose} referralUrl={shareUrl} whiteLabelUrl={whiteLabelUrl} data-sentry-element="ShareWithClientModal" data-sentry-source-file="ShareWithClientButton.tsx" />
    </>;
};
export default ShareWithClientButton;